import { supabase } from '../lib/supabase'
import { fetchInboxDashboardCounts } from './inboxFeed'
import { fetchEodSignoffs, type EodSignoff } from './eodSignoffs'
import {
  buildDivisionPostgrestFilter,
  type OperationalDivision,
} from '../lib/operationalDivisions'
import { isFieldCompleteStatus, isReviewWorkflowStatus } from '../lib/jobStatusWorkflow'
import { computeJobDailyRevenue } from '../lib/jobRevenue'
import { getErrorMessage } from '../lib/errors'

const JOB_SELECT =
  'id, title, status, job_number, scheduled_date, scheduled_end_time, actual_start_time, actual_end_time, quoted_amount, actual_amount, billing_type, operational_division_id, service_type, completion_exception_reason, completion_notes, notes, customer:customers(company_name, phone), property:properties(address, city), billing_contract:contracts!jobs_billing_contract_id_fkey(billing_frequency, billing_method), contract:contracts!jobs_contract_id_fkey(billing_frequency, billing_method)'

export interface EndOfDayJob {
  id: string
  title: string
  status: string
  job_number?: string | null
  scheduled_date?: string | null
  scheduled_end_time?: string | null
  actual_start_time?: string | null
  actual_end_time?: string | null
  quoted_amount?: number | null
  actual_amount?: number | null
  daily_quoted_revenue: number
  daily_actual_revenue: number
  operational_division_id?: string | null
  service_type?: string | null
  completion_exception_reason?: string | null
  completion_notes?: string | null
  notes?: string | null
  billing_type?: string | null
  customer?: { company_name?: string; phone?: string } | null
  property?: { address?: string; city?: string } | null
  contract?: { billing_frequency?: string; billing_method?: string } | null
  techs: string[]
  photos: number
  checklistTotal: number
  checklistDone: number
  jobHours: number
  hasOpenPunch: boolean
  isException: boolean
  isOverdue: boolean
}

export interface EndOfDaySnapshot {
  date: string
  scheduledCount: number
  completedToday: EndOfDayJob[]
  inProgressToday: EndOfDayJob[]
  otherInProgress: EndOfDayJob[]
  notStarted: EndOfDayJob[]
  exceptions: EndOfDayJob[]
  completionRate: number
  totalQuotedRevenue: number
  totalActualRevenue: number
  unassignedInboxCount: number
  unapprovedTimeCount: number
  signoffs: EodSignoff[]
}

export type EndOfDayCoreSnapshot = Omit<
  EndOfDaySnapshot,
  'unassignedInboxCount' | 'unapprovedTimeCount' | 'signoffs'
>

export interface EndOfDayWidgets {
  unassignedInboxCount: number
  unapprovedTimeCount: number
  signoffs: EodSignoff[]
}

function dateBoundsLocal(dateStr: string): { gte: string; lt: string } {
  const parts = dateStr.split('-').map(Number)
  const start = new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0)
  const end = new Date(parts[0], parts[1] - 1, parts[2] + 1, 0, 0, 0, 0)
  return { gte: start.toISOString(), lt: end.toISOString() }
}

function applyDivision<T extends { or: (f: string) => T; eq: (c: string, v: string) => T }>(
  query: T,
  division: OperationalDivision | null
): T {
  if (!division) return query
  return query.or(buildDivisionPostgrestFilter(division))
}

type RawJob = {
  id: string
  title: string
  status: string
  job_number?: string | null
  scheduled_date?: string | null
  scheduled_end_time?: string | null
  actual_start_time?: string | null
  actual_end_time?: string | null
  quoted_amount?: number | null
  actual_amount?: number | null
  billing_type?: string | null
  operational_division_id?: string | null
  service_type?: string | null
  completion_exception_reason?: string | null
  completion_notes?: string | null
  notes?: string | null
  customer?: { company_name?: string; phone?: string } | null
  property?: { address?: string; city?: string } | null
  billing_contract?: { billing_frequency?: string; billing_method?: string } | null
  contract?: { billing_frequency?: string; billing_method?: string } | null
}

type RpcEodPayload = {
  jobs?: RawJob[] | null
  assignments?: Array<{
    job_id: string
    first_name?: string | null
    last_name?: string | null
  }> | null
  photos?: Array<{ job_id: string; count?: number | null }> | null
  tasks?: Array<{ job_id: string; total?: number | null; done?: number | null }> | null
  time_entries?: Array<{
    job_id: string
    total_hours?: number | null
    has_open_punch?: boolean | null
  }> | null
}

function resolveJobBillingContract(
  job: Pick<RawJob, 'billing_contract' | 'contract'>
): { billing_frequency?: string; billing_method?: string } | null {
  return job.billing_contract ?? job.contract ?? null
}

function mapEnrichmentFromRpc(payload: RpcEodPayload) {
  const techsByJob = new Map<string, string[]>()
  for (const row of payload.assignments || []) {
    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim()
    if (!name) continue
    const list = techsByJob.get(row.job_id) || []
    list.push(name)
    techsByJob.set(row.job_id, list)
  }

  const photosByJob = new Map<string, number>()
  for (const row of payload.photos || []) {
    photosByJob.set(row.job_id, Number(row.count || 0))
  }

  const checklistByJob = new Map<string, { done: number; total: number }>()
  for (const row of payload.tasks || []) {
    checklistByJob.set(row.job_id, {
      done: Number(row.done || 0),
      total: Number(row.total || 0),
    })
  }

  const hoursByJob = new Map<string, number>()
  const openPunchByJob = new Map<string, boolean>()
  for (const row of payload.time_entries || []) {
    hoursByJob.set(row.job_id, Number(row.total_hours || 0))
    if (row.has_open_punch) openPunchByJob.set(row.job_id, true)
  }

  return { techsByJob, photosByJob, checklistByJob, hoursByJob, openPunchByJob }
}

function buildEndOfDayJobs(
  rawJobs: RawJob[],
  enrichment: ReturnType<typeof mapEnrichmentFromRpc>
): EndOfDayJob[] {
  const { techsByJob, photosByJob, checklistByJob, hoursByJob, openPunchByJob } = enrichment
  const now = new Date()

  return rawJobs.map((job) => {
    const checklist = checklistByJob.get(job.id) || { done: 0, total: 0 }
    const revenue = computeJobDailyRevenue({
      quoted_amount: job.quoted_amount,
      actual_amount: job.actual_amount,
      scheduled_date: job.scheduled_date,
      actual_end_time: job.actual_end_time,
      billing_type: job.billing_type,
      contract: resolveJobBillingContract(job),
    })

    let isOverdue = false
    if (job.status === 'in_progress' && job.scheduled_end_time) {
      const end = new Date(job.scheduled_end_time)
      if (!Number.isNaN(end.getTime()) && end < now) isOverdue = true
    }

    const hasOpenPunch = openPunchByJob.get(job.id) || false
    const isException =
      !!job.completion_exception_reason ||
      hasOpenPunch ||
      (job.status === 'in_progress' && isOverdue)

    return {
      id: job.id,
      title: job.title,
      status: job.status,
      job_number: job.job_number,
      scheduled_date: job.scheduled_date,
      scheduled_end_time: job.scheduled_end_time,
      actual_start_time: job.actual_start_time,
      actual_end_time: job.actual_end_time,
      quoted_amount: job.quoted_amount,
      actual_amount: job.actual_amount,
      daily_quoted_revenue: revenue.quoted,
      daily_actual_revenue: revenue.actual,
      operational_division_id: job.operational_division_id,
      service_type: job.service_type,
      completion_exception_reason: job.completion_exception_reason,
      completion_notes: job.completion_notes,
      notes: job.notes,
      billing_type: job.billing_type,
      customer: job.customer,
      property: job.property,
      contract: resolveJobBillingContract(job),
      techs: techsByJob.get(job.id) || [],
      photos: photosByJob.get(job.id) || 0,
      checklistTotal: checklist.total,
      checklistDone: checklist.done,
      jobHours: hoursByJob.get(job.id) || 0,
      hasOpenPunch,
      isException,
      isOverdue,
    }
  })
}

async function enrichJobs(rawJobs: RawJob[]): Promise<EndOfDayJob[]> {
  if (rawJobs.length === 0) return []

  const jobIds = rawJobs.map((j) => j.id)

  const [assignmentsRes, photosRes, tasksRes, timeRes] = await Promise.all([
    supabase
      .from('job_assignments')
      .select('job_id, employee:employees(first_name, last_name)')
      .in('job_id', jobIds)
      .is('unassigned_at', null),
    supabase.from('job_photos').select('job_id').in('job_id', jobIds),
    supabase.from('job_tasks').select('job_id, is_done').in('job_id', jobIds),
    supabase
      .from('time_entries')
      .select('job_id, total_hours, clock_out_time')
      .in('job_id', jobIds),
  ])

  const techsByJob = new Map<string, string[]>()
  for (const row of assignmentsRes.data || []) {
    const emp = (row as { employee?: { first_name?: string; last_name?: string } }).employee
    const name = emp ? `${emp.first_name || ''} ${emp.last_name || ''}`.trim() : ''
    if (!name) continue
    const list = techsByJob.get(row.job_id) || []
    list.push(name)
    techsByJob.set(row.job_id, list)
  }

  const photosByJob = new Map<string, number>()
  for (const row of photosRes.data || []) {
    photosByJob.set(row.job_id, (photosByJob.get(row.job_id) || 0) + 1)
  }

  const checklistByJob = new Map<string, { done: number; total: number }>()
  for (const row of tasksRes.data || []) {
    const cur = checklistByJob.get(row.job_id) || { done: 0, total: 0 }
    cur.total += 1
    if (row.is_done) cur.done += 1
    checklistByJob.set(row.job_id, cur)
  }

  const hoursByJob = new Map<string, number>()
  const openPunchByJob = new Map<string, boolean>()
  for (const row of timeRes.data || []) {
    hoursByJob.set(row.job_id, (hoursByJob.get(row.job_id) || 0) + Number(row.total_hours || 0))
    if (!row.clock_out_time) openPunchByJob.set(row.job_id, true)
  }

  return buildEndOfDayJobs(rawJobs, {
    techsByJob,
    photosByJob,
    checklistByJob,
    hoursByJob,
    openPunchByJob,
  })
}

function dedupeRawJobs(rows: RawJob[]): RawJob[] {
  const map = new Map<string, RawJob>()
  for (const row of rows) map.set(row.id, row)
  return Array.from(map.values())
}

function buildSnapshotFromJobs(
  dateStr: string,
  scheduledRaw: RawJob[],
  completedRaw: RawJob[],
  inProgressRaw: RawJob[],
  enriched: EndOfDayJob[],
  extras: {
    unassignedInboxCount: number
    unapprovedTimeCount: number
    signoffs: EodSignoff[]
  }
): EndOfDaySnapshot {
  const byId = new Map(enriched.map((j) => [j.id, j]))

  const completedToday = completedRaw.map((r) => byId.get(r.id)!).filter(Boolean)
  const inProgressToday = scheduledRaw
    .filter((r) => r.status === 'in_progress')
    .map((r) => byId.get(r.id)!)
    .filter(Boolean)
  const otherInProgress = inProgressRaw
    .filter((r) => r.scheduled_date !== dateStr)
    .map((r) => byId.get(r.id)!)
    .filter(Boolean)
  const notStarted = scheduledRaw
    .filter((r) => r.status === 'scheduled' || r.status === 'draft')
    .map((r) => byId.get(r.id)!)
    .filter(Boolean)

  const exceptionIds = new Set<string>()
  for (const job of [...notStarted, ...inProgressToday, ...otherInProgress, ...completedToday]) {
    if (
      notStarted.some((n) => n.id === job.id) ||
      job.completion_exception_reason ||
      job.hasOpenPunch ||
      (job.status === 'in_progress' && job.isOverdue)
    ) {
      exceptionIds.add(job.id)
    }
  }
  const exceptions = Array.from(exceptionIds).map((id) => byId.get(id)!).filter(Boolean)

  const scheduledCount = scheduledRaw.length
  const completedCount = completedToday.length
  const completionRate =
    scheduledCount > 0 ? Math.round((completedCount / scheduledCount) * 100) : completedCount > 0 ? 100 : 0

  const totalQuotedRevenue = completedToday.reduce((s, j) => s + j.daily_quoted_revenue, 0)
  const totalActualRevenue = completedToday.reduce((s, j) => s + j.daily_actual_revenue, 0)

  return {
    date: dateStr,
    scheduledCount,
    completedToday,
    inProgressToday,
    otherInProgress,
    notStarted,
    exceptions,
    completionRate,
    totalQuotedRevenue,
    totalActualRevenue,
    unassignedInboxCount: extras.unassignedInboxCount,
    unapprovedTimeCount: extras.unapprovedTimeCount,
    signoffs: extras.signoffs,
  }
}

async function fetchViaRpc(
  dateStr: string,
  division: OperationalDivision | null
): Promise<{ scheduledRaw: RawJob[]; completedRaw: RawJob[]; inProgressRaw: RawJob[]; enriched: EndOfDayJob[] }> {
  const { data, error } = await supabase.rpc('get_end_of_day_snapshot', {
    p_date: dateStr,
    p_division_id: division?.id || null,
    p_service_types: division?.service_types?.length ? division.service_types : null,
  })
  if (error) throw error

  const payload = (Array.isArray(data) ? data[0] : data) as RpcEodPayload | null
  if (!payload?.jobs) {
    throw Object.assign(new Error('empty end of day snapshot'), { code: 'PGRST116' })
  }

  const allRaw = payload.jobs as RawJob[]
  const scheduledRaw = allRaw.filter((j) => j.scheduled_date === dateStr)
  const bounds = dateBoundsLocal(dateStr)
  const completedRaw = allRaw.filter(
    (j) =>
      isFieldCompleteStatus(j.status) &&
      !!j.actual_end_time &&
      j.actual_end_time >= bounds.gte &&
      j.actual_end_time < bounds.lt
  )
  const inProgressRaw = allRaw.filter(
    (j) => j.status === 'in_progress' && j.scheduled_date !== dateStr
  )

  const enriched = buildEndOfDayJobs(allRaw, mapEnrichmentFromRpc(payload))
  return { scheduledRaw, completedRaw, inProgressRaw, enriched }
}

async function fetchViaClientQueries(
  dateStr: string,
  division: OperationalDivision | null
): Promise<{ scheduledRaw: RawJob[]; completedRaw: RawJob[]; inProgressRaw: RawJob[]; enriched: EndOfDayJob[] }> {
  const bounds = dateBoundsLocal(dateStr)

  let scheduledQ = supabase.from('jobs').select(JOB_SELECT).eq('scheduled_date', dateStr)
  scheduledQ = applyDivision(scheduledQ, division)

  let completedQ = supabase
    .from('jobs')
    .select(JOB_SELECT)
    .gte('actual_end_time', bounds.gte)
    .lt('actual_end_time', bounds.lt)
    .not('actual_end_time', 'is', null)
  completedQ = applyDivision(completedQ, division)

  let inProgressQ = supabase
    .from('jobs')
    .select(JOB_SELECT)
    .eq('status', 'in_progress')
    .or(`scheduled_date.neq.${dateStr},scheduled_date.is.null`)
  inProgressQ = applyDivision(inProgressQ, division)

  const [scheduledRes, completedRes, inProgressRes] = await Promise.all([
    scheduledQ,
    completedQ,
    inProgressQ,
  ])

  if (scheduledRes.error) throw scheduledRes.error
  if (completedRes.error) throw completedRes.error
  if (inProgressRes.error) throw inProgressRes.error

  const scheduledRaw = (scheduledRes.data || []) as RawJob[]
  const completedRaw = ((completedRes.data || []) as RawJob[]).filter((j) =>
    isFieldCompleteStatus(j.status)
  )
  const inProgressRaw = (inProgressRes.data || []) as RawJob[]

  const allRaw = dedupeRawJobs([...scheduledRaw, ...completedRaw, ...inProgressRaw])
  const enriched = await enrichJobs(allRaw)
  return { scheduledRaw, completedRaw, inProgressRaw, enriched }
}

export function jobDetailPath(job: Pick<EndOfDayJob, 'id' | 'status'>): string {
  if (
    isReviewWorkflowStatus(job.status) ||
    job.status === 'pending_review' ||
    job.status === 'under_review'
  ) {
    return `/review/${job.id}`
  }
  return `/jobs/details/${job.id}`
}

export async function fetchUnapprovedTimeCount(
  dateStr: string,
  division: OperationalDivision | null
): Promise<number> {
  const bounds = dateBoundsLocal(dateStr)

  const { count, error } = await supabase
    .from('time_entries')
    .select('id', { count: 'exact', head: true })
    .eq('is_approved', false)
    .gte('clock_in_time', bounds.gte)
    .lt('clock_in_time', bounds.lt)
  if (error) throw error
  if (!division) return count || 0

  const { data, error: listErr } = await supabase
    .from('time_entries')
    .select('id, job:jobs(operational_division_id, service_type)')
    .eq('is_approved', false)
    .gte('clock_in_time', bounds.gte)
    .lt('clock_in_time', bounds.lt)
  if (listErr) throw listErr

  const serviceTypes = division.service_types || []
  return (data || []).filter((row) => {
    const job = (row as { job?: { operational_division_id?: string; service_type?: string } }).job
    if (!job) return false
    if (job.operational_division_id) return job.operational_division_id === division.id
    return !!job.service_type && serviceTypes.includes(job.service_type)
  }).length
}

export async function fetchEndOfDayCore(
  dateStr: string,
  division: OperationalDivision | null
): Promise<EndOfDayCoreSnapshot> {
  const { scheduledRaw, completedRaw, inProgressRaw, enriched } = await fetchViaRpc(
    dateStr,
    division
  ).catch(() => fetchViaClientQueries(dateStr, division))

  return buildSnapshotFromJobs(dateStr, scheduledRaw, completedRaw, inProgressRaw, enriched, {
    unassignedInboxCount: 0,
    unapprovedTimeCount: 0,
    signoffs: [],
  })
}

export async function fetchEndOfDayWidgets(
  dateStr: string,
  division: OperationalDivision | null,
  userId?: string | null
): Promise<EndOfDayWidgets> {
  const [inboxCounts, signoffs, unapprovedTime] = await Promise.all([
    fetchInboxDashboardCounts(userId || null).catch(() => ({
      staffUnassigned: 0,
      overdueTasks: 0,
      myOpenTasks: 0,
      needsResponse: 0,
    })),
    fetchEodSignoffs(dateStr, division?.id).catch(() => [] as EodSignoff[]),
    fetchUnapprovedTimeCount(dateStr, division).catch(() => 0),
  ])

  return {
    unassignedInboxCount: inboxCounts.staffUnassigned,
    unapprovedTimeCount: unapprovedTime,
    signoffs,
  }
}

export async function fetchEndOfDaySnapshot(
  dateStr: string,
  division: OperationalDivision | null,
  userId?: string | null
): Promise<EndOfDaySnapshot> {
  const core = await fetchEndOfDayCore(dateStr, division)
  const widgets = await fetchEndOfDayWidgets(dateStr, division, userId)
  return { ...core, ...widgets }
}

export function isEndOfDayTimeoutError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err || '')
  return /statement timeout|57014|canceling statement/i.test(msg)
}

export function friendlyEndOfDayError(err: unknown): string {
  if (isEndOfDayTimeoutError(err)) {
    return 'End of day data is taking too long to load. This usually clears after a retry, or ask an admin to apply migration 202608261400.'
  }
  return getErrorMessage(err, 'Failed to load end of day data.')
}
